/**
 * Kiosk Device Session Management
 * Handles permanent device pairing session storage and validation
 */

const STORAGE_KEY = 'taginn_kiosk_device_session';

export interface KioskDeviceSession {
  device_id: string;
  session_token: string;
  business_id: string;
  business_name: string;
  device_name: string;
  allowed_modes: string[];
  paired_at: number;
}

/**
 * Save device session to localStorage
 */
export function saveDeviceSession(session: KioskDeviceSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    console.error('Failed to save device session:', error);
  }
}

/**
 * Get device session from localStorage
 */
export function getDeviceSession(): KioskDeviceSession | null {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    return JSON.parse(data) as KioskDeviceSession;
  } catch (error) {
    console.error('Failed to get device session:', error);
    return null;
  }
}

/**
 * Clear device session from localStorage
 */
export function clearDeviceSession(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear device session:', error);
  }
}

/**
 * Check if device has a stored session
 */
export function hasDeviceSession(): boolean {
  return getDeviceSession() !== null;
}

/**
 * Update business name in session (after validation)
 */
export function updateSessionBusinessName(name: string): void {
  const session = getDeviceSession();
  if (session) {
    session.business_name = name;
    saveDeviceSession(session);
  }
}

/**
 * Update allowed modes in session
 */
export function updateSessionAllowedModes(modes: string[]): void {
  const session = getDeviceSession();
  if (session) {
    session.allowed_modes = modes;
    saveDeviceSession(session);
  }
}
